import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ConvexReactClient } from 'convex/react';
import { ConvexAuthProvider, useAuthToken } from '@convex-dev/auth/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import AuthCallback from './components/AuthCallback';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import { Toaster } from './components/ui/sonner';
// HomePage is the most-visited landing route — eager-import so React can
// hydrate the prerendered HTML on the same tick the JS loads, instead of
// flashing the Suspense fallback (which also discards the prerendered DOM
// and forces media to be re-fetched).
import HomePage from './pages/HomePage';

// Lazy-loaded pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const DashboardLegacy = lazy(() => import('./pages/DashboardLegacy'));
const DeploymentsPage = lazy(() => import('./pages/DeploymentsPage'));
const WalletPage = lazy(() => import('./pages/WalletPage'));
const UsagePage = lazy(() => import('./pages/UsagePage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const ModelsPage = lazy(() => import('./pages/ModelsPage'));
const ReferralsPage = lazy(() => import('./pages/ReferralsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const DocsPage = lazy(() => import('./pages/DocsPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const ImpressumPage = lazy(() => import('./pages/ImpressumPage'));
const DeploymentManagementPage = lazy(() => import('./pages/DeploymentManagementPage'));
const DockAgentConsolePage = lazy(() => import('./pages/DockAgentConsolePage'));

// Programmatic hubs & templates (Phase 3)
const IntegrationsHub = lazy(() => import('./pages/IntegrationsHub'));
const IntegrationPage = lazy(() => import('./pages/IntegrationPage'));
const UseCasesHub = lazy(() => import('./pages/UseCasesHub'));
const UseCasePage = lazy(() => import('./pages/UseCasePage'));
const CompareHub = lazy(() => import('./pages/CompareHub'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const SkillsHub = lazy(() => import('./pages/SkillsHub'));
const SkillPage = lazy(() => import('./pages/SkillPage'));

// Blog (Phase 4)
const BlogHub = lazy(() => import('./pages/BlogHub'));
const BlogPost = lazy(() => import('./pages/BlogPost'));

// Personas, Guides, Alternatives (Phase 5)
const PersonasHub = lazy(() => import('./pages/PersonasHub'));
const PersonaPage = lazy(() => import('./pages/PersonaPage'));
const GuidesHub = lazy(() => import('./pages/GuidesHub'));
const GuidePage = lazy(() => import('./pages/GuidePage'));
const AlternativesHub = lazy(() => import('./pages/AlternativesHub'));
const AlternativePage = lazy(() => import('./pages/AlternativePage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Tools
const CostCalculator = lazy(() => import('./pages/CostCalculator'));

// Initialize Convex client
const convex = new ConvexReactClient(process.env.REACT_APP_CONVEX_URL);

// TanStack Query: 30s staleTime keeps dashboard navigation snappy without
// serving stale data. Disable refetchOnWindowFocus to avoid surprise reloads
// when the user alt-tabs back. Single retry on transient failures.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Loading fallback
function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center bg-white">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-orange border-t-transparent" />
    </div>
  );
}

// Routes that must render even when Convex Auth wants to run its sync
// redirect: a password-reset link must never be swallowed by AuthCallback's
// dashboard redirect (e.g. Google-signed-in browser with no custom token).
const AUTH_SYNC_EXEMPT_PATHS = ['/reset-password', '/forgot-password'];

// Sync Convex auth to custom auth
function ConvexAuthSync({ children }) {
  const convexToken = useAuthToken();
  const { token } = useAuth();
  const { pathname } = useLocation();
  if (AUTH_SYNC_EXEMPT_PATHS.includes(pathname)) return children;
  if (convexToken && !token) return <AuthCallback />;
  return children;
}

// Scroll to top on route change, but respect hash fragments
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

function ReferralCapture() {
  const location = useLocation();
  const { captureReferralCode } = useAuth();

  React.useEffect(() => {
    const referralCode = new URLSearchParams(location.search).get('ref');
    if (referralCode) {
      captureReferralCode(referralCode);
    }
  }, [captureReferralCode, location.search]);

  return null;
}

function PublicLightTheme({ children }) {
  const { pathname } = useLocation();
  const { theme, setSessionTheme } = useTheme();
  const previousThemeRef = React.useRef(null);
  const isAppRoute = pathname.startsWith('/dashboard') || pathname === '/dockclaw-admin-x7k9m2';

  React.useEffect(() => {
    if (isAppRoute) {
      if (previousThemeRef.current) {
        setSessionTheme(previousThemeRef.current);
        previousThemeRef.current = null;
      }
      return;
    }

    if (previousThemeRef.current === null && theme !== 'light') {
      previousThemeRef.current = theme;
    }
    if (theme !== 'light') {
      setSessionTheme('light');
    }
  }, [isAppRoute, pathname, setSessionTheme, theme]);

  return children;
}

function AppRouter() {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <PublicLightTheme>
          <ScrollToTop />
          <ReferralCapture />
          <Routes>
        {/* Core pages */}
        <Route path="/" element={<HomePage />} />
        <Route path="/how-it-works" element={<Navigate to="/#how-it-works" replace />} />
        <Route path="/features" element={<Navigate to="/#features" replace />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Integrations */}
        <Route path="/integrations" element={<IntegrationsHub />} />
        <Route path="/integrations/:slug" element={<IntegrationPage />} />

        {/* Use Cases (hub swapped — body shows personas) */}
        <Route path="/use-cases" element={<UseCasesHub />} />
        <Route path="/use-cases/:slug" element={<PersonaPage />} />

        {/* Comparisons */}
        <Route path="/compare" element={<CompareHub />} />
        <Route path="/compare/:slug" element={<ComparePage />} />

        {/* Skills */}
        <Route path="/skills" element={<SkillsHub />} />
        <Route path="/skills/:slug" element={<SkillPage />} />

        {/* Blog */}
        <Route path="/blog" element={<BlogHub />} />
        <Route path="/blog/:slug" element={<BlogPost />} />

        {/* Personas / Workflows (hub swapped — body shows use cases) */}
        <Route path="/for" element={<PersonasHub />} />
        <Route path="/for/:slug" element={<PersonaPage />} />
        <Route path="/workflows" element={<PersonasHub />} />
        <Route path="/workflows/:slug" element={<UseCasePage />} />

        {/* Guides */}
        <Route path="/guides" element={<GuidesHub />} />
        <Route path="/guides/:slug" element={<GuidePage />} />

        {/* Alternatives */}
        <Route path="/alternatives" element={<AlternativesHub />} />
        <Route path="/alternatives/:slug" element={<AlternativePage />} />

        {/* Auth */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* App */}
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/dashboard/deployments" element={<DeploymentsPage />} />
        <Route path="/dashboard/wallet" element={<WalletPage />} />
        <Route path="/dashboard/usage" element={<UsagePage />} />
        <Route path="/dashboard/analytics" element={<AnalyticsPage />} />
        <Route path="/dashboard/models" element={<ModelsPage />} />
        <Route path="/dashboard/referrals" element={<ReferralsPage />} />
        <Route path="/dashboard/legacy" element={<DashboardLegacy />} />
        <Route path="/dashboard/dockagent/:id" element={<DockAgentConsolePage />} />
        <Route path="/dashboard/settings" element={<WalletPage />} />
        <Route path="/dashboard/deployment/:id" element={<DeploymentManagementPage />} />
        <Route path="/dockclaw-admin-x7k9m2" element={<AdminDashboard />} />

        {/* Tools */}
        <Route path="/tools/cost-calculator" element={<CostCalculator />} />

        {/* Legal */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/impressum" element={<ImpressumPage />} />

        {/* 404 — must stay last; catches /v2 and anything else not above. */}
        <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </PublicLightTheme>
      </Suspense>
    </RouteErrorBoundary>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <ConvexAuthProvider client={convex}>
            <AuthProvider>
              <div className="App min-h-screen bg-white text-[#29251e]">
                <BrowserRouter>
                  <ConvexAuthSync>
                    <AppRouter />
                    <Toaster />
                  </ConvexAuthSync>
                </BrowserRouter>
              </div>
            </AuthProvider>
          </ConvexAuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
